import { apply, onceToday, toast } from '../core/effects';
import { bus } from '../core/bus';
import { hasGame, S } from '../core/game';
import { DAY_ORDER, type GameState } from '../core/state';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { weatherNow, type Weather } from '../content/weather';
import { elderCard } from '../render/elderCard';

/** One reply the player can pick, Mom's answers to it and (optionally) the next turn. */
export interface MomReply {
  /** The player's line (EN practice); "@card" sends a good-morning picture back. */
  me: string;
  tone: 'warm' | 'ok' | 'rude';
  /** Affection for a warm reply (+3…+8, granted once a day). */
  love?: number;
  mom: string[];
  next?: MomReply[];
}
export interface MomText {
  icon: string;
  /** Mom's opening messages; "@card" is a good-morning picture (長輩圖). */
  msgs: string[];
  card?: { theme: number; big: string; sub: string };
  replies: MomReply[];
}
export type MomKey = keyof ReturnType<typeof m>['family']['texts'];

export interface ThreadMsg {
  side: 'me' | 'them' | 'sep';
  text?: string;
  img?: string;
}

const KEEP_DAYS = 14;
const MAX_TURNS = 3;

/** Absolute game day (dayIndex never resets; same numbering as the calendar). */
export function dayNum(s: GameState = S()): number {
  return s.dayIndex;
}

/** Which text Mom sends on a given day: the weather first, then some variety. Pure. */
export function chooseMomText(day: number, w: Weather): MomKey {
  switch (w.kind) {
    case 'rain':
      return 'rain';
    case 'storm':
      return 'storm';
    case 'cool':
      return 'cool';
    case 'typhoon':
      return 'typhoon';
    case 'sunny':
      if (w.temp >= 31) return 'hot';
      if (day % 2 === 0) return 'sunny';
      break;
    case 'cloudy':
      break;
  }
  // Friday is a school day (test luck), Saturday a family dinner, Sunday a good-morning card.
  return (['exam', 'dinner', 'card'] as const)[day % DAY_ORDER.length];
}

/** Optional P.S. about the balcony garden. */
function gardenPs(s: GameState, w: Weather): '' | 'water' | 'ripe' {
  const planted = s.garden.filter((p) => p.crop);
  if (planted.some((p) => p.water >= 3)) return 'ripe';
  if (planted.length && w.kind !== 'rain' && w.kind !== 'storm' && w.kind !== 'typhoon') return 'water';
  return '';
}

function textOf(key: string): MomText | undefined {
  const base = key.split('|')[0];
  return (m().family.texts as Record<string, MomText>)[base];
}

/** Today's entry, created on demand (dayStart, or lazily after loading a save). */
export function ensureToday(): GameState['momTexts'][number] | null {
  if (!hasGame()) return null;
  const s = S();
  const day = dayNum(s);
  let e = s.momTexts.find((x) => x.day === day);
  if (!e) {
    const w = weatherNow();
    const ps = gardenPs(s, w);
    e = { day, key: chooseMomText(day, w) + (ps ? `|${ps}` : ''), picks: [] };
    s.momTexts.push(e);
    s.momTexts = s.momTexts.filter((x) => x.day > day - KEEP_DAYS).sort((a, b) => a.day - b.day);
    e = s.momTexts.find((x) => x.day === day)!;
  }
  return e;
}

/** Walk the reply tree along the picks: the messages so far and the choices left. */
export function resolve(key: string, picks: readonly number[]): { msgs: ThreadMsg[]; choices: MomReply[] } {
  const t = textOf(key);
  if (!t) return { msgs: [], choices: [] };
  const card = (c: { theme: number; big: string; sub: string }) => (typeof document === 'undefined' ? '' : elderCard(c.theme, c.big, c.sub));
  const msgs: ThreadMsg[] = t.msgs.map((x) => (x === '@card' && t.card ? { side: 'them', img: card(t.card) } : { side: 'them', text: x }));
  const ps = key.split('|')[1] as keyof ReturnType<typeof m>['family']['ps'] | undefined;
  if (ps && m().family.ps[ps]) msgs.push({ side: 'them', text: m().family.ps[ps] });
  let level: MomReply[] = t.replies;
  for (const p of picks) {
    const r = level[p];
    if (!r) return { msgs, choices: [] };
    msgs.push(r.me === '@card' ? { side: 'me', img: card(m().family.replyCard) } : { side: 'me', text: r.me });
    for (const x of r.mom) msgs.push({ side: 'them', text: x });
    if (r.tone === 'rude') msgs.push({ side: 'them', text: m().family.sad });
    level = r.next ?? [];
  }
  return { msgs, choices: picks.length >= MAX_TURNS ? [] : level };
}

function dayLabel(day: number, today: number): string {
  if (day === today) return m().family.today;
  const id = DAY_ORDER[day % DAY_ORDER.length];
  return `${m().hud.week(Math.floor(day / DAY_ORDER.length) + 1)} · ${m().days[id].name}`;
}

// --- Live state: Mom's answers appear one by one with a typing indicator --------------

let hidden = 0;
let timer: ReturnType<typeof setTimeout> | undefined;
let rerender: () => void = () => {};

export function bindMomRender(fn: () => void): void {
  rerender = fn;
}

function reveal(): void {
  clearTimeout(timer);
  if (hidden <= 0) return;
  timer = setTimeout(() => {
    hidden--;
    sfx('pop');
    rerender();
    reveal();
  }, 850);
}

export function momTyping(): boolean {
  return hidden > 0;
}

/** Every stored day (oldest first) with separators; Mom's newest answers may still be "typing". */
export function momThread(): ThreadMsg[] {
  if (!hasGame()) return [];
  ensureToday();
  const s = S();
  const today = dayNum(s);
  const out: ThreadMsg[] = [];
  for (const e of [...s.momTexts].sort((a, b) => a.day - b.day)) {
    out.push({ side: 'sep', text: `${textOf(e.key)?.icon ?? '📩'} ${dayLabel(e.day, today)}` });
    out.push(...resolve(e.key, e.picks).msgs);
  }
  return hidden > 0 ? out.slice(0, out.length - hidden) : out;
}

/** The reply buttons for today's text (empty once the talk is over or Mom is typing). */
export function momChoices(): { text: string; run: () => void }[] {
  const e = ensureToday();
  if (!e || hidden > 0) return [];
  return resolve(e.key, e.picks).choices.map((r, i) => ({
    text: r.me === '@card' ? m().family.sendCard : r.me,
    run: () => pickReply(i),
  }));
}

/** Mom's text today still waits for a first reply. */
export function momUnanswered(): boolean {
  const e = hasGame() ? S().momTexts.find((x) => x.day === dayNum()) : undefined;
  return !!e && e.picks.length === 0;
}

export function momPreview(): string {
  const all = momThread().filter((x) => x.side !== 'sep');
  const last = all[all.length - 1];
  return last ? (last.img ? '🖼️' : last.side === 'me' ? `${m().family.you}: ${last.text}` : last.text!) : m().family.previewNone;
}

/** Apply one reply: affection (warm once a day, rude always stings) and Mom's answers. */
export function pickReply(i: number): void {
  const e = ensureToday();
  if (!e) return;
  const before = resolve(e.key, e.picks);
  const r = before.choices[i];
  if (!r) return;
  e.picks.push(i);
  if (r.tone === 'warm' && r.love && onceToday('fam.momWarm')) {
    apply({ love: { mom: r.love } }, { float: false });
    toast(m().family.loveUp(r.love));
  } else if (r.tone === 'rude') {
    apply({ love: { mom: -3 } }, { float: false });
    toast(m().family.loveDown);
  }
  hidden = r.mom.length + (r.tone === 'rude' ? 1 : 0);
  sfx('pop');
  bus.emit('change');
  rerender();
  reveal();
}

/** Reset the live animation (tests, day changes). */
export function resetMomLive(): void {
  hidden = 0;
  clearTimeout(timer);
}
